import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import DateRange from 'components/Form/DateRange';
import Text from 'components/Form/Text';
import { SubstanceDrugsSupplementalUsageItemModel } from 'forms/sections/substance/drugs/supplementalUsage/types';
import {
  CommonOnUpdate, CommonScrollIntoView, CommonOnError, CommonRequired
} from 'types/props';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';

interface Props extends Partial<SubstanceDrugsSupplementalUsageItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  scrollIntoView?: CommonScrollIntoView
  onError?: CommonOnError
  required?: CommonRequired
  formType?: string
}

export default class DrugSupplementalUse extends ValidationElement<Props> {
  update = (updateValues: Partial<SubstanceDrugsSupplementalUsageItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        DrugUsed: this.props.DrugUsed,
        DateRange: this.props.DateRange,
        NumberOfTimesUsed: this.props.NumberOfTimesUsed,
        Comment: this.props.Comment,
        ...updateValues,
      });
    }
  };

  updateDrugUsed = (values: SubstanceDrugsSupplementalUsageItemModel['DrugUsed']) => {
    this.update({ DrugUsed: values });
  };

  updateDateRange = (values: SubstanceDrugsSupplementalUsageItemModel['DateRange']) => {
    this.update({ DateRange: values });
  };

  updateNumberOfTimesUsed = (values: SubstanceDrugsSupplementalUsageItemModel['NumberOfTimesUsed']) => {
    this.update({ NumberOfTimesUsed: values });
  };

  updateComment = (values: SubstanceDrugsSupplementalUsageItemModel['Comment']) => {
    this.update({ Comment: values });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="drug-supplemental-use">
        <Field
          attribute={`${attribute}.DrugUsed`}
          title={i18n.t('substance.drugs.supplementalUsage.heading.drugUsed')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            attribute={`${attribute}.DrugUsed`}
            name="DrugUsed"
            className="drug-used"
            {...this.props.DrugUsed}
            onUpdate={this.updateDrugUsed}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.DateRange`}
          title={i18n.t('substance.drugs.supplementalUsage.heading.dateRange')}
          adjustFor="daterange"
          help="substance.drugs.supplementalUsage.help.dateRange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            attribute={`${attribute}.DateRange`}
            name="DateRange"
            className="date-range"
            minDateEqualTo
            {...this.props.DateRange}
            onUpdate={this.updateDateRange}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          attribute={`${attribute}.NumberOfTimesUsed`}
          title={i18n.t('substance.drugs.supplementalUsage.heading.numberOfTimesUsed')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.NumberOfTimesUsed`}
            name="NumberOfTimesUsed"
            className="number-of-times-used"
            {...this.props.NumberOfTimesUsed}
            onUpdate={this.updateNumberOfTimesUsed}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

      </div>
    ) : (
      <div className="drug-supplemental-use">
        <Field
          attribute={`${attribute}.DrugUsed`}
          title={i18n.t('substance.drugs.supplementalUsage.heading.drugUsed')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            attribute={`${attribute}.DrugUsed`}
            name="DrugUsed"
            className="drug-used"
            {...this.props.DrugUsed}
            onUpdate={this.updateDrugUsed}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.DateRange`}
          title={i18n.t('substance.drugs.supplementalUsage.heading.dateRange')}
          adjustFor="daterange"
          help="substance.drugs.supplementalUsage.help.dateRange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            attribute={`${attribute}.DateRange`}
            name="DateRange"
            className="date-range"
            minDateEqualTo
            {...this.props.DateRange}
            onUpdate={this.updateDateRange}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          attribute={`${attribute}.NumberOfTimesUsed`}
          title={i18n.t('substance.drugs.supplementalUsage.heading.numberOfTimesUsed')}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.NumberOfTimesUsed`}
            name="NumberOfTimesUsed"
            className="number-of-times-used"
            {...this.props.NumberOfTimesUsed}
            onUpdate={this.updateNumberOfTimesUsed}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Comments
          title={i18n.t('substance.drugs.supplementalPublicSafety.heading.comment')}
          name="Comment"
          className="supplemental-public-safety-comment"
          attribute={`${attribute}.Comment`}
          {...this.props.Comment}
          onUpdate={this.updateComment}
          required={false}
        />
      </div>
    );
  }
}

DrugSupplementalUse.defaultProps = {
  onError: (value, arr) => arr,
  formType: '',
};
